'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  linkWithCredential,
  fetchSignInMethodsForEmail,
  EmailAuthProvider,
} from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { useStore } from '@/lib/store';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { UserProfile, DEFAULT_PROFILE_SETTINGS } from '@/types/profile';
import { validateUsername, isUsernameTaken } from '@/lib/usernameUtils';
import { getDeviceFingerprint, getDeviceInfo, isKnownDevice, rememberDevice } from '@/lib/deviceTracking';

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, username?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  linkGoogleAccount: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const setStoreUser = useStore((state) => state.setUser);

  const loadUserProfile = async (user: User) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const profileData = userDoc.data() as UserProfile;

        // Sync Google photo if user has Google linked and no custom photo
        const hasGoogleProvider = user.providerData.some(p => p.providerId === 'google.com');
        const googlePhoto = user.providerData.find(p => p.providerId === 'google.com')?.photoURL;

        if (hasGoogleProvider && googlePhoto && !profileData.hasCustomPhoto && profileData.photoURL !== googlePhoto) {
          // Update Firestore with the latest Google photo
          await updateDoc(doc(db, 'users', user.uid), {
            photoURL: googlePhoto,
          });
          profileData.photoURL = googlePhoto;
        }

        // Check if account is scheduled for deletion
        if (profileData.scheduledForDeletion) {
          // User signed in before deletion date - restore account
          await updateDoc(doc(db, 'users', user.uid), {
            scheduledForDeletion: false,
            deletionDate: null,
            deletionRequestedAt: null,
          });

          // Show recovery message
          const { toast } = await import('sonner');
          toast.success('Welcome back! Your account deletion has been cancelled.');

          // Remove deletion flags from profile data
          const { scheduledForDeletion, deletionDate, deletionRequestedAt, ...cleanProfile } = profileData;

          setUserProfile({
            ...DEFAULT_PROFILE_SETTINGS,
            ...cleanProfile,
            email: user.email || profileData.email,
          } as UserProfile);
        } else {
          setUserProfile({
            ...DEFAULT_PROFILE_SETTINGS,
            ...profileData,
            email: user.email || profileData.email,
          } as UserProfile);
        }
      } else {
        // Create default profile for existing user without profile
        const defaultProfile: Partial<UserProfile> = {
          ...DEFAULT_PROFILE_SETTINGS,
          email: user.email || undefined,
          username: user.email?.split('@')[0],
          createdAt: new Date().toISOString(),
        };
        await setDoc(doc(db, 'users', user.uid), defaultProfile);
        setUserProfile(defaultProfile as UserProfile);
      }
    } catch (error) {
      // Set minimal profile on error
      setUserProfile({
        ...DEFAULT_PROFILE_SETTINGS,
        email: user.email || undefined,
        createdAt: new Date().toISOString(),
      } as UserProfile);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      setStoreUser(user);

      if (user) {
        await loadUserProfile(user);

        // Check for new device login
        try {
          const fingerprint = getDeviceFingerprint();
          const known = await isKnownDevice(user.uid, fingerprint);

          if (!known && user.email) {
            // Remember the device before emailing so a slow/failed email
            // request can't cause duplicate alerts from other tabs
            await rememberDevice(user.uid, fingerprint);

            // Get device info and send alert email
            const deviceInfo = await getDeviceInfo();

            // Send new device alert email
            fetch('/api/auth/send-device-alert', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: user.email,
                deviceInfo: {
                  ...deviceInfo,
                },
                logoutToken: '', // Optional: implement logout token if needed
              }),
            }).catch(() => {
              // Silently fail
            });
          }
        } catch (error) {
          // Silently fail device tracking
        }
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, [setStoreUser]);

  const signIn = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      throw error;
    }
  };

  const signUp = async (email: string, password: string, username?: string) => {
    try {
      // Validate and check username availability
      const usernameToUse = username || email.split('@')[0];
      const validation = await validateUsername(usernameToUse);

      if (!validation.valid) {
        const error: any = new Error(validation.error);
        error.suggestions = validation.suggestions;
        throw error;
      }

      const result = await createUserWithEmailAndPassword(auth, email, password);

      if (result.user) {
        // Generate verification code
        const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();
        const verificationCodeExpiry = new Date(Date.now() + 10 * 60 * 1000).toISOString(); // 10 minutes
        const accountDeletionDate = new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(); // 48 hours from now

        const newProfile: Partial<UserProfile> = {
          ...DEFAULT_PROFILE_SETTINGS,
          username: usernameToUse.toLowerCase(),
          email,
          createdAt: new Date().toISOString(),
          emailVerified: false,
          verificationCode,
          verificationCodeExpiry,
          pendingDeletionAt: accountDeletionDate, // Will be deleted if not verified within 48 hours
        };

        await setDoc(doc(db, 'users', result.user.uid), newProfile);
        setUserProfile(newProfile as UserProfile);

        // Send verification email
        try {
          await fetch('/api/auth/send-verification', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, code: verificationCode }),
          });
        } catch (emailError) {
          // Don't throw - user can resend later
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      if (result.user) {
        const userDoc = await getDoc(doc(db, 'users', result.user.uid));
        if (!userDoc.exists()) {
          // Generate unique username from display name or email
          let baseUsername = result.user.displayName?.toLowerCase().replace(/\s+/g, '_') ||
                           result.user.email?.split('@')[0] ||
                           'user';

          // Clean username
          baseUsername = baseUsername.replace(/[^a-z0-9_-]/g, '');

          // Check if username is taken and get suggestions if needed
          let finalUsername = baseUsername;
          if (await isUsernameTaken(baseUsername)) {
            // Add random number to make it unique
            const randomNum = Math.floor(Math.random() * 9999);
            finalUsername = `${baseUsername}${randomNum}`;
          }

          const newProfile: Partial<UserProfile> = {
            ...DEFAULT_PROFILE_SETTINGS,
            email: result.user.email || undefined,
            username: finalUsername.toLowerCase(),
            displayName: result.user.displayName || undefined,
            photoURL: result.user.photoURL || undefined,
            createdAt: new Date().toISOString(),
            emailVerified: true, // Google has already verified the email
          };
          await setDoc(doc(db, 'users', result.user.uid), newProfile);
          // Set the profile immediately so user doesn't get redirected to verify page
          setUserProfile(newProfile as UserProfile);
        } else {
          // User exists - update their profile with Google photo and verify email if needed
          const existingProfile = userDoc.data() as UserProfile;
          const updates: Partial<UserProfile> = {};

          // Sync Google photo if user hasn't uploaded a custom photo
          if (!existingProfile.hasCustomPhoto && result.user.photoURL) {
            updates.photoURL = result.user.photoURL;
          }

          // Mark email as verified since they signed in with Google
          if (!existingProfile.emailVerified) {
            updates.emailVerified = true;
            // Remove pending deletion since they verified via Google
            updates.pendingDeletionAt = null as any;
          }

          if (Object.keys(updates).length > 0) {
            await updateDoc(doc(db, 'users', result.user.uid), updates);
            // Reload the profile to get the updated data
            await loadUserProfile(result.user);
          }
        }
      }
    } catch (error: any) {
      // Handle account-exists-with-different-credential error
      if (error.code === 'auth/account-exists-with-different-credential') {
        try {
          const email = error.customData?.email;
          if (!email) throw error;

          // Get the existing sign-in methods for this email
          const methods = await fetchSignInMethodsForEmail(auth, email);

          if (methods.includes('password')) {
            // Show message to user that they need to link accounts
            const { toast } = await import('sonner');
            toast.error(
              'An account with this email already exists. Please sign in with your email/password first, then link your Google account in Settings.',
              { duration: 7000 }
            );
            throw new Error('ACCOUNT_EXISTS_LINK_REQUIRED');
          } else {
            // Try to sign in with the existing provider
            throw error;
          }
        } catch (linkError) {
          throw linkError;
        }
      }

      throw error;
    }
  };

  const linkGoogleAccount = async () => {
    if (!user) {
      throw new Error('No user logged in');
    }

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // Update profile with Google photo if available
      if (result.user.photoURL && !userProfile?.photoURL) {
        await updateDoc(doc(db, 'users', user.uid), {
          photoURL: result.user.photoURL,
        });
        setUserProfile(prev => prev ? { ...prev, photoURL: result.user.photoURL } : null);
      }

      const { toast } = await import('sonner');
      toast.success('Google account linked successfully!');
    } catch (error: any) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw error;
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) {
      throw new Error('No user logged in');
    }

    try {
      // If updating username, validate it first
      if (updates.username) {
        const validation = await validateUsername(updates.username, user.uid);
        if (!validation.valid) {
          const error: any = new Error(validation.error);
          error.suggestions = validation.suggestions;
          throw error;
        }
        // Ensure username is lowercase
        updates.username = updates.username.toLowerCase();
      }

      await updateDoc(doc(db, 'users', user.uid), updates);

      // Update local state
      setUserProfile(prev => prev ? { ...prev, ...updates } : null);
    } catch (error) {
      throw error;
    }
  };

  const refreshProfile = async () => {
    if (!user) return;
    await loadUserProfile(user);
  };

  const value = {
    user,
    userProfile,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    linkGoogleAccount,
    logout,
    resetPassword,
    updateProfile,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}